
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/errors/use-toast';
import { usePageMutations } from './pages';
import { useQueryClient } from '@tanstack/react-query';
import { pageKeys } from './pages/workbook-query-keys';
import { convertToWebP, isConvertibleToWebP, getOptimalConversionSettings } from '@/utils/image-conversion';

interface UsePageCoverImageOptions {
  pageId: string;
  initialCoverImageUrl?: string;
}

export function usePageCoverImage({ pageId, initialCoverImageUrl }: UsePageCoverImageOptions) {
  const [coverImageUrl, setCoverImageUrl] = useState<string | undefined>(initialCoverImageUrl);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();
  const { updatePage } = usePageMutations();
  const queryClient = useQueryClient();

  // Function to invalidate all relevant queries when cover image changes
  const invalidateAllQueries = () => {
    // Invalidate workbook queries
    queryClient.invalidateQueries({ queryKey: ['workbook-projects'], exact: false });
    // Invalidate page queries
    queryClient.invalidateQueries({ queryKey: pageKeys.all, exact: false });
    // Also invalidate any pages queries for the old naming convention
    queryClient.invalidateQueries({ queryKey: ['pages'], exact: false });
  };

  const uploadCoverImage = async (file: File): Promise<string> => {
    try {
      setIsUploading(true);
      
      // Create a unique file path for this page's cover image
      const userId = (await supabase.auth.getSession()).data.session?.user?.id;
      if (!userId) throw new Error('User not authenticated');
      
      // 🚀 PHASE 1: AUTOMATIC WEBP CONVERSION
      let processedFile = file;
      let conversionInfo: string | null = null;

      // Convert to WebP if it's a convertible format and not already WebP
      if (file.type !== 'image/webp' && isConvertibleToWebP(file)) {
        try {
          console.log('[CoverImage] Converting to WebP:', file.name);
          
          const conversionSettings = getOptimalConversionSettings(file, 'cover');
          const result = await convertToWebP(file, conversionSettings);
          
          processedFile = result.file;
          conversionInfo = `Converted from ${result.originalFormat} to WebP (${result.compressionRatio.toFixed(1)}% size reduction)`;
          
          console.log('[CoverImage] WebP conversion successful:', {
            originalSize: result.originalSize,
            newSize: result.newSize,
            savings: `${result.compressionRatio.toFixed(1)}%`
          });
        } catch (error) {
          console.warn('[CoverImage] WebP conversion failed, using original:', error);
          // Continue with original file if conversion fails
        }
      } else if (file.type === 'image/webp') {
        console.log('[CoverImage] File already in WebP format');
      } else {
        console.log('[CoverImage] File type not convertible to WebP, using original');
      }
      
      // Generate unique filename with proper extension
      const extension = processedFile.type === 'image/webp' ? 'webp' : (processedFile.name.split('.').pop() || 'jpg');
      const fileName = `${pageId}_${Date.now()}.${extension}`;
      const filePath = `${userId}/${fileName}`;
      
      // Upload the processed file to the page_covers bucket
      const { error: uploadError, data } = await supabase.storage
        .from('page_covers')
        .upload(filePath, processedFile, {
          upsert: true,
          contentType: processedFile.type
        });
      
      if (uploadError) throw uploadError;
      
      // Get the public URL for the uploaded file
      const { data: { publicUrl } } = supabase.storage
        .from('page_covers')
        .getPublicUrl(filePath);
      
      // Update the page metadata to include the cover image URL
      await updatePage({
        id: pageId,
        updates: {
          metadata: {
            coverImage: publicUrl
          }
        }
      });
      
      setCoverImageUrl(publicUrl);
      
      // Invalidate all queries to ensure the cover image updates everywhere
      invalidateAllQueries();
      
      // Dispatch a custom event to notify other components about the cover image change
      const event = new CustomEvent('page-cover-image-updated', {
        detail: { pageId, coverImageUrl: publicUrl }
      });
      document.dispatchEvent(event);
      
      // Show success toast with conversion info
      const baseMessage = "Your page cover image has been updated successfully.";
      const description = conversionInfo ? `${baseMessage} ${conversionInfo}` : baseMessage;
      
      toast({
        title: "Cover image updated",
        description,
      });
      
      return publicUrl;
    } catch (error) {
      console.error('Error uploading cover image:', error);
      toast({
        title: "Upload failed",
        description: "There was an error uploading your cover image. Please try again.",
        variant: "destructive",
      });
      throw new Error('Failed to upload cover image');
    } finally {
      setIsUploading(false);
    }
  };

  const removeCoverImage = async () => {
    try {
      setIsUploading(true);
      
      // Update the page metadata to remove the cover image URL
      await updatePage({
        id: pageId,
        updates: {
          metadata: {
            coverImage: null
          }
        }
      });
      
      setCoverImageUrl(undefined);
      
      // Invalidate all queries to ensure the cover image removal updates everywhere
      invalidateAllQueries();
      
      // Dispatch a custom event to notify other components about the cover image removal
      const event = new CustomEvent('page-cover-image-updated', {
        detail: { pageId, coverImageUrl: null }
      });
      document.dispatchEvent(event);
      
      toast({
        title: "Cover image removed",
        description: "Your page cover image has been removed successfully.",
      });
    } catch (error) {
      console.error('Error removing cover image:', error);
      toast({
        title: "Error",
        description: "There was an error removing your cover image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return {
    coverImageUrl,
    isUploading,
    uploadCoverImage,
    removeCoverImage
  };
}
